import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ClipboardCheck, CalendarPlus, User, Users } from "lucide-react";
import { MOTRI_AVATAR } from "@/components/motri/ChatMessage";
import { getTranslatedValue } from "@/components/TranslatableField";

export function TripActionButtons() {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [regOpen, setRegOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [regClubOpen, setRegClubOpen] = useState(false);
  const [planClubOpen, setPlanClubOpen] = useState(false);
  const [approvedClubs, setApprovedClubs] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) return;
    api.get("/api/my-clubs")
      .then(res => {
        const clubs = (res.data || []).filter(g => g.membership_status === "approved");
        setApprovedClubs(clubs);
      })
      .catch(() => {});
  }, [isAuthenticated]);

  const plan = user?.subscription_plan || user?.plan || "rookie";
  const role = user?.role || "user";
  const hasMotri = plan !== "rookie" || role === "owner" || role === "admin";
  const hasClubs = approvedClubs.length > 0;

  const handleVisitorClick = () => navigate("/regista-te");

  if (!isAuthenticated) {
    return (
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={handleVisitorClick} className="action-bar-btn" data-testid="register-trip-btn">
          <ClipboardCheck className="h-4 w-4 sm:mr-1" />
          <span className="btn-label">{t("actions.register_trip", "Registar passeio")}</span>
        </Button>
        <Button variant="outline" onClick={handleVisitorClick} className="action-bar-btn" data-testid="plan-trip-btn">
          <CalendarPlus className="h-4 w-4 sm:mr-1" />
          <span className="btn-label">{t("actions.plan_trip", "Planear passeio")}</span>
        </Button>
      </div>
    );
  }

  const MenuItem = ({ icon: Icon, label, onClick, testId, children }) => (
    <button
      onClick={onClick}
      className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-foreground/90 hover:bg-accent hover:text-foreground rounded-md transition-colors text-left"
      data-testid={testId}
    >
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <span className="flex-1">{label}</span>
      {children}
    </button>
  );

  const ClubItem = ({ club, type, onDone }) => (
    <button
      onClick={() => {
        const tripType = type === "register" ? "completed" : "planned";
        navigate(`/passeios-grupo/editar?group=${club.club_id}&tab=create&type=${tripType}`);
        onDone();
      }}
      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground/90 hover:bg-accent hover:text-foreground rounded-md transition-colors text-left"
      data-testid={`${type}-club-${club.club_id}`}
    >
      {club.logo_url ? (
        <img src={club.logo_url} alt="" className="h-5 w-5 rounded-full object-cover shrink-0" />
      ) : (
        <Users className="h-4 w-4 text-muted-foreground shrink-0" />
      )}
      <span className="truncate">{typeof club.name === 'object' ? getTranslatedValue(club.name, i18n.language?.split('-')[0] || 'pt') : club.name}</span>
    </button>
  );

  const ClubsInline = ({ clubs, type, parentSetOpen }) => (
    <>
      <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{t("actions.register_club_trip", "Passeio de clube")}</p>
      {clubs.map(club => (
        <ClubItem key={club.club_id} club={club} type={type} onDone={() => parentSetOpen(false)} />
      ))}
    </>
  );

  const PopoverMenu = ({ type, clubs, open, setOpen, clubSubOpen, setClubSubOpen }) => (
    <>
      <p className="px-3 pt-1.5 pb-1 text-[10px] uppercase tracking-wider font-semibold text-primary sm:hidden">
        {type === "register" ? t("actions.register_trip", "Registar passeio") : t("actions.plan_trip", "Planear passeio")}
      </p>
      <MenuItem
        icon={User}
        label={type === "register" ? t("actions.register_personal", "Passeio pessoal") : t("actions.plan_personal", "Passeio pessoal")}
        onClick={() => {
          navigate(type === "register" ? "/meus-passeios/editar?tab=create&type=completed" : "/meus-passeios/editar?tab=create&type=planned");
          setOpen(false);
        }}
        testId={`${type}-personal-option`}
      />
      {hasClubs && (
        <ClubsInline clubs={clubs} type={type} parentSetOpen={setOpen} />
      )}
      <div className="h-px bg-border/50 my-1" />
      <MenuItem
        icon={() => <img src={MOTRI_AVATAR} alt="Motri" className="h-4 w-4 rounded-full object-cover" />}
        label={type === "register" ? t("actions.talk_motri_register", "Falar com o Motri") : t("actions.talk_motri_plan", "Falar com o Motri")}
        onClick={() => {
          navigate(hasMotri ? `/motri?auto=${type === "register" ? "register_trip" : "plan_trip"}` : "/motri/apresentacao");
          setOpen(false);
        }}
        testId={`${type}-motri-option`}
      />
    </>
  );

  return (
    <div className="flex items-center gap-2">
      {/* REGISTAR PASSEIO */}
      <Popover open={regOpen} onOpenChange={(o) => { setRegOpen(o); if (!o) setRegClubOpen(false); }}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="action-bar-btn" data-testid="register-trip-btn">
            <ClipboardCheck className="h-4 w-4 sm:mr-1" />
            <span className="btn-label">{t("actions.register_trip", "Registar passeio")}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-1.5" sideOffset={2} collisionPadding={8}>
          <PopoverMenu type="register" clubs={approvedClubs} open={regOpen} setOpen={setRegOpen} clubSubOpen={regClubOpen} setClubSubOpen={setRegClubOpen} />
        </PopoverContent>
      </Popover>

      {/* PLANEAR PASSEIO */}
      <Popover open={planOpen} onOpenChange={(o) => { setPlanOpen(o); if (!o) setPlanClubOpen(false); }}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="action-bar-btn" data-testid="plan-trip-btn">
            <CalendarPlus className="h-4 w-4 sm:mr-1" />
            <span className="btn-label">{t("actions.plan_trip", "Planear passeio")}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-1.5" sideOffset={2} collisionPadding={8}>
          <PopoverMenu type="plan" clubs={approvedClubs} open={planOpen} setOpen={setPlanOpen} clubSubOpen={planClubOpen} setClubSubOpen={setPlanClubOpen} />
        </PopoverContent>
      </Popover>
    </div>
  );
}
